/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  FormState,
  FormStateBuilder,
  PrinterBusinessService,
  REPORT_CONSTRUCTION_SCHEDULE,
  ReportService,
  whenCreateMode,
} from '@gh/builder-shared';
import { SystemRoleBit, systemRolesBitSet } from '@gh/builder-shared-data';
import { createDialogRoute, getDialogInitData, toAppFormControl } from '@gh/common-shared';
import { RouteDataService } from '@gh/core-data';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  NotificationService,
  WaitingService,
} from '@gh/core-ui';

import { ScheduleListBusinessService } from '../../resources/schedule-list-business.service';
import {
  isSupervisorSelectTypeSelect,
  ScheduleConstructionReportDialogConfig,
  ScheduleConstructionReportDialogRequest,
  SupervisorSelectType,
} from '../../resources/schedule-list.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';

const supervisorTypes = {
  ALL: 'All',
  SELECTED: 'Selected',
  UNASSIGNED: 'Unassigned',
};

@Component({
  standalone: false,
  selector: 'gh-schedule-construction-report-dialog',
  templateUrl: 'schedule-construction-report-dialog.component.html',
  styleUrls: ['schedule-construction-report-dialog.component.scss'],
})
export class ScheduleConstructionReportDialogComponent implements OnInit, DialogInput<ScheduleConstructionReportDialogConfig> {
  input: ScheduleConstructionReportDialogConfig;
  form: FormState<ScheduleConstructionReportDialogRequest>;
  isPrintDisabled: boolean;
  printUrl: string;
  @ViewChild('printLink') printLink: ElementRef;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private waitingService: WaitingService,
              private printerBusinessService: PrinterBusinessService,
              private notificationService: NotificationService,
              private formStateBuilder: FormStateBuilder,
              private businessService: ScheduleListBusinessService,
              private reportService: ReportService,
    ) {
  }

  ngOnInit(): void {
    getDialogInitData(this.dialogData);
    this.form = this.formStateBuilder.buildPlain({
      mode: FormMode.CreateMode,
      enabled: whenCreateMode(),
      value: {
        worksheetFormat: false,
        supervisorSelectType: SupervisorSelectType.All,
        onlyActiveJobs: true,
        showCompletedTasks: false,
      },
      children: {
        supervisorSelectType: {
          required: true,
        },
        supervisor: {
          required: true,
          enabled: false,
        },
        worksheetFormat: void 0,
        onlyActiveJobs: void 0,
        onlyNotCompletedTasks: void 0,
        showCompletedTasks: void 0,
      },
    });
  }
  loadSupervisors($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const fullQuery = {
      ...query,
      filter: { name: query.filter, showInactive: false, systemRoles: systemRolesBitSet(SystemRoleBit.Supervisor)},
    };
    load(this.businessService.findAllSupervisorsAsDataSource(fullQuery));
  }

  onSupervisorSelectTypeChange({ value }: MatRadioChange): void {
    const supervisorListControl = toAppFormControl(getControl(this.form.group, 'supervisor'));
    if (isSupervisorSelectTypeSelect(value)) {
      supervisorListControl.updateSchema({enabled: true});
    } else {
      supervisorListControl.updateSchema({enabled: false});
      supervisorListControl.setValue('');
    }
  }

  onPreview(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.reportService.generateUrlAndShowDialog(
      REPORT_CONSTRUCTION_SCHEDULE,
      [
        { name: 'Supervisor',
          value: this.form.getControl('supervisor')?.value || null,
          id: true,
        },
        { name: 'SupervisorChoise',
          value: supervisorTypes[this.form.getControl('supervisorSelectType').value],
        },
        { name: 'Format',
          value: this.form.getControl('worksheetFormat')?.value ? 'Worksheet' : 'Basic',
        },
        { name: 'OnlyActiveJobs',
          value: !!this.form.getControl('onlyActiveJobs')?.value},
        { name: 'OnlyNotCompletedTasks',
          value:  !!this.form.getControl('onlyNotCompletedTasks')?.value,
        },
        {
          name: 'IsFromBuilderPortal',
          value: true,
        },
        { name: 'ShowCompletedTasks',
          value:  !!this.form.getControl('showCompletedTasks')?.value,
        },
      ],
    ).subscribe();
  }

  onCancel(): void {
    this.dialogRef.close();
  }
  onPrint(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isPrintDisabled = true;
    this.reportService.generatePrintUrlAndShowDialog(
      {
        supervisorId: this.form.getControl('supervisor')?.value || null,
        supervisorChoise: supervisorTypes[this.form.getControl('supervisorSelectType').value],
        format: this.form.getControl('worksheetFormat')?.value ? 'Worksheet' : 'Basic',
        onlyActiveJobs: !!this.form.getControl('onlyActiveJobs')?.value,
        onlyNotCompletedTasks: !!this.form.getControl('onlyNotCompletedTasks')?.value,
        showCompletedTasks: !!this.form.getControl('showCompletedTasks')?.value,
      },
    ).subscribe((url: string) => {
      this.printUrl = url;
      setTimeout(() => {
        this.printLink.nativeElement.click();
        this.isPrintDisabled = false;
      },         100);
    },          (err) => {
      this.notificationService.processAll(err);
      this.isPrintDisabled = false;
    });
  }
}

export const scheduleConstructionReportDialogRoute = createDialogRoute({
  component: ScheduleConstructionReportDialogComponent,
  init: {
    service: ScheduleListBusinessService,
    selector: 'initScheduleConstructionReportDialog',
  },
});
